import FAQItems from "../components/FAQItems";

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const TEAL_DIM = "rgba(79,205,196,0.15)";
const TEAL_BORDER = "rgba(79,205,196,0.35)";
const CARD_BORDER = "rgba(255,255,255,0.04)";
const AMBER = "#E0B057";

function H2({ id, children }) {
  return <h2 id={id} style={{ fontFamily: "var(--display)", fontWeight: 700, fontSize: "clamp(22px, 3vw, 28px)", color: TEXT, margin: "48px 0 16px", letterSpacing: "-0.01em", lineHeight: 1.2 }}>{children}</h2>;
}
function P({ children }) {
  return <p style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.85, margin: "0 0 18px" }}>{children}</p>;
}
function Li({ children }) {
  return <li style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.8, marginBottom: 8 }}>{children}</li>;
}
function Strong({ children }) {
  return <strong style={{ color: TEXT, fontWeight: 700 }}>{children}</strong>;
}
function Code({ children }) {
  return <code style={{ fontFamily: "var(--mono)", fontSize: 14, color: TEXT, background: "rgba(255,255,255,0.06)", padding: "2px 6px", borderRadius: 4 }}>{children}</code>;
}
function A({ href, children, external }) {
  if (external) {
    return <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: TEAL, textDecoration: "underline", fontWeight: 600 }}>{children}</a>;
  }
  return <a href={href} style={{ color: TEAL, textDecoration: "underline", fontWeight: 600 }}>{children}</a>;
}
function PullQuote({ children }) {
  return (
    <blockquote style={{ margin: "32px 0", padding: "20px 24px", borderLeft: `3px solid ${TEAL}`, fontFamily: "var(--display)", fontSize: 20, fontWeight: 600, color: TEXT, lineHeight: 1.4, fontStyle: "normal" }}>
      {children}
    </blockquote>
  );
}

/* ── Visual 1: BidStrategyTable ── the three strategies, and which one holds a cost line */
function BidStrategyTable() {
  const rows = [
    {
      name: "Manual: Max bid",
      who: "You set the ceiling",
      target: "Yes, you set it",
      note: "The only documented bid ceiling. You choose the number and it holds.",
    },
    {
      name: "Conversions bid cap",
      who: "You cap, the system optimizes",
      target: "Capped, not guaranteed",
      note: "Conversions objective only. Billed per click or per impression, not per conversion. The conversion event cannot be changed after the campaign is created.",
    },
    {
      name: "Maximize results",
      who: "The system decides",
      target: "No",
      note: "Selected by default on eligible new ad groups. Optimizes for result volume within budget.",
    },
  ];
  return (
    <div style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px", overflow: "hidden" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>Three bid strategies, one question: does it hold a cost line?</div>
      <div style={{ fontSize: 10, color: TEXT_DIM, marginBottom: 16, fontFamily: "var(--body)" }}>Per OpenAI&#39;s Help Center, accessed September 9, 2026.</div>
      <div className="blog-scroll-wrap" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", margin: "0 -4px" }}>
        <table style={{ width: "100%", minWidth: 620, borderCollapse: "collapse", fontFamily: "var(--body)" }}>
          <thead>
            <tr>
              {["Strategy", "Who sets the bid", "Guarantees a cost target?", "What to know"].map((h) => (
                <th key={h} style={{ textAlign: "left", fontSize: 11, fontWeight: 700, color: TEXT_DIM, textTransform: "uppercase", letterSpacing: "0.06em", padding: "10px 12px", borderBottom: `1px solid rgba(255,255,255,0.12)`, whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name}>
                <td style={{ fontSize: 14, fontWeight: 700, color: TEXT, padding: "12px", borderBottom: `1px solid ${CARD_BORDER}`, whiteSpace: "nowrap" }}>{r.name}</td>
                <td style={{ fontSize: 14, color: TEXT_DIM, padding: "12px", borderBottom: `1px solid ${CARD_BORDER}` }}>{r.who}</td>
                <td style={{ fontSize: 14, fontWeight: 700, color: r.target === "No" ? AMBER : TEXT, padding: "12px", borderBottom: `1px solid ${CARD_BORDER}`, whiteSpace: "nowrap" }}>{r.target}</td>
                <td style={{ fontSize: 13, color: TEXT_DIM, padding: "12px", borderBottom: `1px solid ${CARD_BORDER}`, lineHeight: 1.55 }}>{r.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Visual 2: VerificationGap ── the 1-day window against a B2B cycle, to scale */
function VerificationGap() {
  return (
    <div style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>The view-through window against a B2B buying cycle</div>
      <div style={{ fontSize: 10, color: TEXT_DIM, marginBottom: 20, fontFamily: "var(--body)" }}>Drawn to scale across 180 days. The view-through bar is one day wide.</div>
      <div style={{ position: "relative", height: 120 }}>
        <div style={{ position: "absolute", left: 0, right: 0, top: 34, height: 1, background: "rgba(255,255,255,0.12)" }} />
        <div style={{ position: "absolute", left: 0, top: 10, width: "0.55%", minWidth: 3, height: 24, background: TEAL, borderRadius: 2 }} />
        <div style={{ position: "absolute", left: "1.5%", top: 12, fontSize: 12, fontWeight: 700, color: TEAL, fontFamily: "var(--body)", whiteSpace: "nowrap" }}>
          View-through window: 1 day (or off)
        </div>
        <div style={{ position: "absolute", left: "16.6%", top: 52, width: "83.4%", height: 24, background: TEAL_DIM, border: `1px solid ${TEAL_BORDER}`, borderRadius: 2 }} />
        <div style={{ position: "absolute", left: "16.6%", top: 84, fontSize: 12, fontWeight: 700, color: TEXT, fontFamily: "var(--body)" }}>
          A B2B buying cycle: 30 to 180 days
        </div>
        <div style={{ position: "absolute", left: 0, bottom: 0, fontSize: 10, color: TEXT_DIM, fontFamily: "var(--mono)" }}>Day 0</div>
        <div style={{ position: "absolute", right: 0, bottom: 0, fontSize: 10, color: TEXT_DIM, fontFamily: "var(--mono)" }}>Day 180</div>
      </div>
    </div>
  );
}

/* ── Visual 3: VolumeVsValue ── what "maximize results" means at two ACVs */
function VolumeVsValue() {
  const cards = [
    {
      label: "What the bidder optimizes",
      value: "Result count",
      note: "More results inside the budget. Every result is weighted the same.",
      accent: AMBER,
    },
    {
      label: "What a B2B account cares about",
      value: "Result quality",
      note: "One enterprise trial can be worth twenty free-tier signups. The bidder cannot see that difference.",
      accent: TEAL,
    },
    {
      label: "Where they diverge",
      value: "Thin volume",
      note: "At a handful of conversions a month, volume optimization has almost no signal to learn from.",
      accent: TEXT,
    },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, margin: "16px 0 32px" }}>
      {cards.map((c) => (
        <div key={c.label} style={{ background: "#0f0f11", border: `1px solid ${CARD_BORDER}`, borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: TEXT_DIM, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "var(--body)", marginBottom: 10 }}>{c.label}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: c.accent, fontFamily: "var(--display)", letterSpacing: "-0.01em", marginBottom: 8 }}>{c.value}</div>
          <div style={{ fontSize: 13, color: TEXT_DIM, lineHeight: 1.6, fontFamily: "var(--body)" }}>{c.note}</div>
        </div>
      ))}
    </div>
  );
}

const faqs = [
  {
    q: "Is Maximize results turned on by default in ChatGPT Ads?",
    a: "Yes, for new ad groups. OpenAI's Help Center states that for eligible new ad groups, Maximize results is selected by default. Existing ad groups keep whatever strategy they were built with. OpenAI does not publish what makes an ad group eligible, so the only reliable way to know which of your ad groups are on it is to open each one and check the bid strategy field.",
  },
  {
    q: "Does Maximize results hit a target CPA?",
    a: "No. OpenAI states that Maximize results prioritizes result volume and does not guarantee delivery against a specific CPA, CPC, ROAS, or other cost-efficiency target at this time. It spends the budget to produce as many results as it can. If you need a cost ceiling, Manual: Max bid is the documented option that holds one.",
  },
  {
    q: "Should B2B advertisers use automated bidding on ChatGPT Ads?",
    a: "Not as a default, and not yet. Automated bidding works best with dense conversion data and roughly equal-value conversions. Most B2B accounts have neither: a handful of conversions a month, and lead values that differ by an order of magnitude. Start on Manual: Max bid, gather cost data you control, then test automation deliberately against it.",
  },
  {
    q: "What is a view-through conversion in ChatGPT Ads?",
    a: "It is counted when someone converts within one day after seeing an eligible ad impression, and no qualifying ad click receives credit for the same conversion. It shows in the attribution breakdown on the Conversions column and as an optional View-through conversions column. OpenAI states that the reporting window you choose changes reporting only, not campaign optimization, bidding, or billing.",
  },
  {
    q: "Can I change the ChatGPT Ads view-through window?",
    a: "Only on or off. OpenAI's reporting settings offer a view-through window of 1 Day or 0 Day (disabled), set independently of the 7-, 14- or 30-day click-through window. There is no longer view-through option. For businesses with buying cycles measured in weeks or months, one day will miss most influenced conversions.",
  },
  {
    q: "Do view-through conversions get added to my Conversions total?",
    a: "Yes, when the view-through window is set to 1 Day: OpenAI's Conversions total then includes click-through and eligible view-through conversions. Set it to 0 Day and the total counts click-through only. This is the detail most likely to cause a reporting error, because a blended total reads as stronger click performance than it is. When you report what clicks produced, use the separate Click-through conversions column.",
  },
  {
    q: "How do I switch a ChatGPT Ads ad group off automated bidding?",
    a: "Open the ad group, find the bid strategy section, and change it to Manual: Max bid. Do this per ad group rather than assuming a campaign-level setting covers it, since the default is applied at the ad group level. Audit every ad group created since the strategy shipped, because any of them may have launched on the default without an explicit choice.",
  },
];

function Content() {
  return (
    <article>
      {/* Overview box */}
      <div style={{ background: TEAL_DIM, border: `1px solid ${TEAL_BORDER}`, borderRadius: 12, padding: "24px 28px", margin: "0 0 36px" }}>
        <P>
          <Strong><A href="/glossary/chatgpt-ads">ChatGPT Ads</A> now selects an automated bid strategy for you by default, and it does not promise a cost target.</Strong> In the same wave, OpenAI shipped view-through conversion reporting on a window that tops out at one day. For B2B advertisers, those two changes pull against each other: the platform is optimizing for volume, on a measurement window too short to tell you whether that volume was worth buying.
        </P>
        <ul style={{ paddingLeft: 20, margin: "0 0 4px" }}>
          <Li><Strong>Default-on.</Strong> Maximize results is selected by default on eligible new ad groups</Li>
          <Li><Strong>No cost line.</Strong> It explicitly does not guarantee a CPA, CPC, or ROAS target</Li>
          <Li><Strong>One day.</Strong> The view-through window is one day or off, and cannot be lengthened</Li>
          <Li><Strong>In the total.</Strong> With the window on, view-through counts toward Conversions; a separate column breaks it out</Li>
          <Li><Strong>The B2B problem.</Strong> Thin conversion volume plus unequal lead values is the worst case for volume optimization</Li>
        </ul>
      </div>

      <H2 id="what-changed">What changed, and what OpenAI does not promise</H2>
      <P>
        <Strong>Maximize results is a bid strategy that hands bid decisions to ChatGPT Ads.</Strong> OpenAI describes it as a strategy that lets the platform <A href="https://help.openai.com/en/articles/20001425-maximize-results-bid-strategy" external>automatically set and adjust bids on your behalf</A>, optimizing toward the campaign goal within your budget.
      </P>
      <P>
        Two sentences in that documentation matter more than the rest. The first: <Strong>for eligible new ad groups, Maximize results is selected by default.</Strong> You do not opt in. Any ad group you have built since it shipped may already be running it.
      </P>
      <P>
        The second is the one to read twice. OpenAI writes that Maximize results prioritizes result volume and <Strong>does not guarantee</Strong> delivery against a specific CPA, CPC, ROAS, or other cost-efficiency target at this time. That is not marketing hedging. It is the platform telling you plainly that this strategy has no cost ceiling.
      </P>
      <PullQuote>
        A default that optimizes for volume and declines to promise a cost target is a reasonable product decision. It is a poor fit for an account that measures success in pipeline rather than clicks.
      </PullQuote>

      <H2 id="why-b2b">Why a volume-maximizing default is riskier for B2B</H2>
      <P>
        <Strong>Automated bidding needs dense, comparable conversions to work well. Most B2B accounts have neither.</Strong> The two conditions that make volume optimization safe are the two conditions B2B accounts most reliably violate.
      </P>
      <VolumeVsValue />
      <P>
        The first condition is <Strong>data density</Strong>. Bid automation learns from conversion events. An ecommerce account producing hundreds of purchases a week gives the system a rich signal within days. A <A href="/blog/chatgpt-search-ads-b2b">B2B account running ChatGPT Ads</A> may produce a handful of demo requests in a month. There is very little for a volume optimizer to learn from, and a great deal of budget it can spend while trying.
      </P>
      <P>
        The second is <Strong>value parity</Strong>. Volume optimization treats every result as interchangeable. In B2B they are not. A free-tier signup and an enterprise trial request both register as one conversion, and they can differ in eventual value by an order of magnitude. The bidder cannot see that difference, so it will happily buy more of whichever is cheaper to acquire.
      </P>
      <P>
        This is why <A href="/glossary/cost-per-action">cost-per-action</A> targets exist as a concept. A cost target is how you tell a bidder that not all volume is worth the same money. Maximize results is documented as not honoring one.
      </P>

      <H2 id="verification-gap">The measurement window that cannot verify it</H2>
      <P>
        <Strong>ChatGPT Ads added view-through conversion reporting on a window that cannot exceed one day.</Strong> OpenAI defines a view-through conversion as one counted when someone converts within one day after seeing an eligible ad impression, where no qualifying click receives credit for the same conversion.
      </P>
      <P>
        The window cannot be lengthened. OpenAI's <A href="https://help.openai.com/en/articles/20001214-measure-results" external>Measure Results documentation</A> offers a view-through window of 1 Day or 0 Day (disabled), set independently of the 7-, 14- or 30-day click-through window. You can lengthen the click window. You cannot lengthen this one.
      </P>
      <VerificationGap />
      <P>
        Set that against how B2B actually buys. Our own <A href="/blog/chatgpt-search-ads-b2b">early-results breakdown</A> puts B2B cycles in the 30 to 180 day range, which matches most operators&#39; experience of a committee purchase with a security review in it. A buyer who sees your ad on a Tuesday and books a demo three weeks later is invisible to a one-day view window.
      </P>
      <P>
        So the two changes arrive pointing in opposite directions. The bidder is now free to chase volume without a cost ceiling, and the new measurement column that might have shown you the influenced conversions it produced only looks one day back.
      </P>
      <PullQuote>
        You have been given more automation and a shorter lens to inspect it through. For a channel with a long consideration window, that is the wrong pairing.
      </PullQuote>
      <P>
        One further detail worth getting right in your reporting. <Strong>With the view-through window on, the Conversions total includes view-through conversions</Strong>, and the separate <Code>Click-through conversions</Code> and <Code>View-through conversions</Code> columns break it apart. OpenAI says the reporting window changes reporting only, not bidding or billing. Report the click-through column when you mean what clicks produced, and treat the one-day view-through figure as a floor on influence, not a measure of it. If the pixel is not firing cleanly to begin with, none of these columns mean much; the <A href="/blog/how-to-install-chatgpt-ads-pixel-in-google-tag-manager">pixel install walkthrough</A> covers that setup, and our <A href="/blog/how-to-check-chatgpt-ads-pixel-is-working">six-check pixel test</A> confirms it works.
      </P>

      <H2 id="eligible-ad-groups">What &ldquo;eligible new ad groups&rdquo; does not tell you</H2>
      <P>
        <Strong>OpenAI says the default applies to eligible new ad groups and never defines eligibility.</Strong> The documentation names no campaign type, no objective, no account status, and no date from which the behavior applies.
      </P>
      <P>
        That matters operationally. You cannot reason from the docs to a list of which of your ad groups flipped to the default and which did not. There is no published rule to apply, which means the only reliable method is inspection: open each ad group in the <A href="/blog/how-to-use-chatgpt-ads-manager">Ads Manager</A> and read the bid strategy field.
      </P>
      <P>
        If you run a handful of campaigns this is ten minutes of work. If you manage ChatGPT Ads across a client roster it is a standing audit item, and worth adding to whatever checklist governs your <A href="/blog/how-to-report-on-chatgpt-ad-campaigns">campaign reporting routine</A>.
      </P>

      <H2 id="three-strategies">The three bid strategies, side by side</H2>
      <P>
        <Strong>Only one documented strategy holds a cost line you set.</Strong> ChatGPT Ads currently offers CPM, CPC, and conversion-optimized objectives, and the bid strategy sits underneath whichever objective you pick.
      </P>
      <BidStrategyTable />
      <P>
        The Conversions row carries a constraint that catches people out. A conversion-optimized campaign uses one selected conversion event, and <Strong>that event cannot be changed</Strong> after the campaign is created. Nor can an existing campaign switch objective in place; OpenAI's route is to clone it into a new Conversions campaign. Choose the event deliberately, because the decision is durable; our <A href="/blog/how-to-set-up-chatgpt-ads-conversion-campaigns">conversion campaign setup guide</A> walks through which event to pick.
      </P>
      <P>
        For most B2B accounts starting out, Manual: Max bid is the honest first setting. It produces cost data you own rather than cost data the system chose for you, and it gives you a baseline to test automation against later. The <A href="/blog/how-to-set-up-chatgpt-ad-campaign">campaign setup walkthrough</A> covers where this sits in the build flow.
      </P>

      <H2 id="unit-economics">What this costs at B2B unit economics</H2>
      <P>
        <Strong>The risk is not that automated bidding overspends. It is that it optimizes toward the wrong result and you find out a quarter later.</Strong>
      </P>
      <P>
        Work it through with your own numbers rather than a benchmark. Take your monthly conversion count on the channel, your average contract value, and the share of conversions that are genuinely qualified. If your qualified share varies a lot between conversion types, a volume optimizer will drift toward the cheap ones, because cheap and plentiful is exactly what it was asked to find.
      </P>
      <P>
        Two accounts can run identical spend at an identical cost per conversion and produce completely different pipeline, purely because one bought more of the expensive-but-qualified type. Volume optimization has no way to prefer the second account&#39;s mix. Neither does a one-day view window help you notice the drift, since most of the qualified conversions land outside it.
      </P>
      <P>
        This is the same reasoning behind measuring <A href="/glossary/incrementality">incrementality</A> rather than raw conversion counts, and it is why our <A href="/blog/how-to-measure-roi-for-chatgpt-ads">ROI measurement framework</A> starts from pipeline rather than platform-reported conversions. For the cost side of the picture, the <A href="/blog/chatgpt-ads-cost">ChatGPT Ads cost reference</A> covers what budgets buy at current rates.
      </P>

      <H2 id="what-to-do">What to change in your account this week</H2>
      <P>
        <Strong>Five things, in priority order.</Strong> None of them takes long, and the first one is the one that matters.
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>Audit every ad group.</Strong> Open each one and read the bid strategy field. Do not assume a campaign-level setting covers it; the default is applied per ad group.</Li>
        <Li><Strong>Switch to Manual: Max bid</Strong> anywhere you have not deliberately chosen automation, then set the ceiling from your own cost data. If the bid strength meter beside it reads May not deliver, our guide to <A href="/blog/chatgpt-ads-no-impressions">fixing zero impressions</A> covers what to check before you raise it.</Li>
        <Li><Strong>Split your reporting columns.</Strong> Keep Conversions and VTA (1d) visibly separate, and make sure nobody downstream is summing them into a single performance number.</Li>
        <Li><Strong>Check your conversion events.</Strong> If a conversion-optimized campaign is pointed at a low-value event, you cannot change it later. That decision is durable.</Li>
        <Li><Strong>Write down your baseline</Strong> before you test automation. Cost per qualified conversion for the last full month, from your own <A href="/blog/how-to-track-chatgpt-ads-in-ga4">analytics</A> and consistent <A href="/glossary/utm-parameters">UTM parameters</A>, not the platform&#39;s numbers.</Li>
      </ul>
      <PullQuote>
        OpenAI has not published what makes an ad group eligible for the default. Until it does, the only way to know which of yours flipped is to open them and look.
      </PullQuote>
      <div style={{ background: TEAL_DIM, border: `1px solid ${TEAL_BORDER}`, borderRadius: 12, padding: "20px 24px", margin: "8px 0 32px" }}>
        <P>
          If your ChatGPT Ads conversions are not reaching your CRM yet, the measurement side of this is the part to fix first. The <A href="/integrations/chatgpt-ads">AI-Advisors ChatGPT Ads integration</A> installs the conversion pixel and overlays paid performance against your organic AI citations, so you can see which conversions the channel actually influenced. Available on the Growth plan and above.
        </P>
      </div>

      <H2 id="what-we-dont-know">What we do not know yet</H2>
      <P>
        <Strong>Three open questions, stated as open.</Strong> The documentation does not answer them, and neither will we by guessing.
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>Eligibility.</Strong> OpenAI has not published what makes an ad group eligible for the default.</Li>
        <Li><Strong>Performance.</Strong> There is no public data comparing Maximize results against manual bidding on B2B accounts. Anyone claiming a lift figure today is extrapolating.</Li>
        <Li><Strong>Where the window goes.</Strong> Conversions campaigns billed on impressions now optimize toward conversions driven by views as well as clicks, so view-through has become an optimization input for that billing option. The reporting window still stops at one day, and no longer window has been announced.</Li>
      </ul>
      <P>
        We will update this post when any of the three resolves. The broader platform picture, including Ads Manager self-service availability for advertisers in more than 60 countries and the objectives on offer, sits in the <A href="/blog/chatgpt-ads-complete-guide">complete ChatGPT Ads guide</A>.
      </P>

      {/* FAQPage JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map((faq) => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a },
        })),
      }) }} />

      {/* FAQ section */}
      <H2 id="faq">Frequently Asked Questions</H2>
      <FAQItems faqs={faqs} />

      {/* Related Reading */}
      <H2 id="related">Related Reading</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><A href="/blog/chatgpt-ads-conversion-tracking">ChatGPT Ads Conversion Tracking Is Real: What OpenAI&#39;s April 30 Privacy Policy Update Tells You About the Roadmap</A></Li>
        <Li><A href="/blog/chatgpt-search-ads-b2b">ChatGPT Search Ads for B2B: Early Results, What&#39;s Working, and What Isn&#39;t</A></Li>
        <Li><A href="/blog/how-to-set-up-chatgpt-ad-campaign">How to Set Up a ChatGPT Ad Campaign in 8 Steps (September 2026)</A></Li>
        <Li><A href="/blog/chatgpt-ads-cost">How Much Do ChatGPT Ads Cost? CPM Range, CPC Bids (October 2026 Update)</A></Li>
      </ul>
    </article>
  );
}

const post = {
  slug: "chatgpt-ads-maximize-results-b2b",
  title: "ChatGPT Ads Now Bids for You by Default. B2B Should Opt Out.",
  excerpt: "ChatGPT Ads now picks your bids by default and reports view-through on a window capped at one day. For B2B, those two changes pull in opposite directions.",
  metaDescription: "Maximize results is on by default in ChatGPT Ads and guarantees no cost target. Why B2B advertisers should switch to manual bidding, and what to check first.",
  date: "2026-09-09",
  lastUpdated: "2026-10-13",
  readTime: "10 min read",
  category: "AI Ads",
  tags: ["ChatGPT Ads bidding", "Maximize results bid strategy", "ChatGPT Ads view-through conversions", "B2B ChatGPT Ads", "automated bidding", "ChatGPT Ads measurement"],
  fiveA: "ads",
  Content,
};

export default post;
